import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import bowser from 'yola-bowser';
import { i18next, site, siteLocales, utils, dialogs } from '@yola/ws-sdk';
import { Modal, Panel, PanelGroup, reactHookForm, designSystem } from '@yola/ws-ui';
import getLanguages from 'src/js/modules/website-settings/helpers/get-languages';
import getCountries from 'src/js/modules/website-settings/helpers/get-countries';
import getLanguageData from 'src/js/modules/website-settings/helpers/get-language-data';
import dialogTypes from 'src/js/modules/dialogs/constants/dialog-types';

const { useForm, useWatch } = reactHookForm;
const {
  ControlGroup,
  TextAreaField,
  Stack,
  LanguageSelectField,
  DialogHeader,
  DialogHeaderTitle,
  DialogButtonGroup,
  DialogButton,
  DialogButtonGroupSeparator,
  Box,
} = designSystem;

const DIALOG_WIDTH = 800;
const DIALOG_HEIGHT = 654;
const GUIDELINES_FIELD_NAME = 'guidelines';
const APPLY_TO_FIELD_NAME = 'applyToLocale';

function isObjectEmpty(obj) {
  return Object.keys(obj).length === 0;
}

const defaultLocalesProp = [];

function TranslationGuidelinesDialog({
  onAfterSave = utils.noop,
  onAfterCancel = utils.noop,
  onAfterFail = utils.noop,
  locales = defaultLocalesProp,
  activeLocale = undefined,
}) {
  const dispatch = useDispatch();
  const guidelines = useSelector(site.selectors.getAiTranslationGuidelines);
  const mainLocale = useSelector(siteLocales.selectors.getMainLocale);
  const usedLocales = useSelector(siteLocales.selectors.getSiteLocales);
  const isMobile = bowser.mobile;

  const localeOptions = useMemo(() => {
    const languagesMap = getLanguages();
    const countriesMap = getCountries();
    const allLocales = [...new Set([...Object.keys(guidelines || {}), ...locales, ...usedLocales])];

    return allLocales.map((locale) => {
      const langData = getLanguageData(locale, languagesMap, countriesMap);
      return {
        value: langData.code,
        label: langData.name,
        iconComponent: langData.country.iconComponent,
      };
    });
  }, [guidelines, locales, usedLocales]);

  const { control, handleSubmit } = useForm({
    defaultValues: {
      [GUIDELINES_FIELD_NAME]: guidelines,
      [APPLY_TO_FIELD_NAME]: activeLocale || mainLocale,
    },
  });

  const currentLocale = useWatch({ control, name: APPLY_TO_FIELD_NAME });
  const currentLocaleOption = localeOptions.find((option) => option.value === currentLocale);

  const handleDialogSubmit = async (values) => {
    const guidelinesByLocale = values[GUIDELINES_FIELD_NAME] || {};
    const filteredGuidelines = Object.fromEntries(
      Object.entries(guidelinesByLocale).filter(([, value]) => value && value.trim())
    );

    if (
      isEqual(guidelines, filteredGuidelines) ||
      (!guidelines && isObjectEmpty(filteredGuidelines))
    ) {
      onAfterSave();
      return;
    }

    dispatch(dialogs.actions.show(dialogTypes.TRANSLATION_GUIDELINES_PROCESSING_DIALOG));

    try {
      await dispatch(
        site.thunks.saveSiteProperties({
          aiTranslationGuidelines: filteredGuidelines,
        })
      );
      onAfterSave();
    } catch (error) {
      dispatch(
        dialogs.actions.show(dialogTypes.TRANSLATION_GUIDELINES_ERROR_DIALOG, {
          onDismiss: onAfterFail,
        })
      );
    }
  };

  const onSubmit = handleSubmit(handleDialogSubmit);

  return (
    <Modal
      className="ws-translation-guidelines-dialog"
      width={DIALOG_WIDTH}
      height={DIALOG_HEIGHT}
      centered
      resizable={false}
      draggable={false}
      overlay="visible"
      fullscreen={bowser.mobile}
      onOverlayClick={onAfterCancel}
      onPressEsc={onAfterCancel}
    >
      <PanelGroup height="100%">
        <Panel theme="gray-100" corners="squared" align="middle">
          <DialogHeader>
            <DialogHeaderTitle>{i18next.t('Yola AI: Translation guidelines')}</DialogHeaderTitle>
          </DialogHeader>
        </Panel>
        <Panel theme="white" className="ws-translation-guidelines-dialog__body">
          <Box
            paddingTop="spacing-s"
            paddingBottom="spacing-s"
            paddingLeft={isMobile ? 'spacing-xs' : 'spacing-m'}
            paddingRight={isMobile ? 'spacing-xs' : 'spacing-m'}
          >
            <ControlGroup title={i18next.t('Guidelines')} fullheight>
              <Stack gap="spacing-3-xs">
                <LanguageSelectField
                  control={control}
                  name={APPLY_TO_FIELD_NAME}
                  options={localeOptions}
                  defaultValue={currentLocaleOption}
                />
                <TextAreaField
                  placeholder={i18next.t('Add custom preferences for the translation')}
                  key={currentLocale}
                  control={control}
                  name={`${GUIDELINES_FIELD_NAME}.${currentLocale}`}
                  maxLength={10000}
                  fullheight
                />
              </Stack>
            </ControlGroup>
          </Box>
        </Panel>
        <Panel>
          <DialogButtonGroup>
            <DialogButton onClick={onSubmit}>{i18next.t('Submit')}</DialogButton>
            <DialogButtonGroupSeparator />
            <DialogButton onClick={onAfterCancel}> {i18next.t('Cancel')}</DialogButton>
          </DialogButtonGroup>
        </Panel>
      </PanelGroup>
    </Modal>
  );
}

TranslationGuidelinesDialog.propTypes = {
  onAfterSave: PropTypes.func,
  onAfterCancel: PropTypes.func,
  onAfterFail: PropTypes.func,
  locales: PropTypes.arrayOf(PropTypes.string),
  activeLocale: PropTypes.string,
};

export default TranslationGuidelinesDialog;
